import React, { useEffect, useState } from "react";
import { Button, Empty } from "antd";
import { TagsOutlined, PlusOutlined } from "@ant-design/icons";
import useMenu from "../hooks/useMenu";
import { nowDate } from "../components/Customs/nowDate";
import { GeneralService } from "../services/GeneralService";
import { notify } from "../components/Customs/NotifyProvider";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";
import DCTable from "../components/drink-cat/DCTable";
import CreateDrinkCat from "../components/drink-cat/CreateDrinkCat";

const MODULE_KEY = "drink_category";

const DrinkCat = () => {
  const { loading, createMenu, updateMenu } = useMenu();

  const [fC, setFC] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);

  const fetchFC = async () => {
    if (!canViewModule(MODULE_KEY)) return;

    try {
      const data = await GeneralService.get("drink_category");
      setFC(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      notify.error(error?.message || "Failed to fetch categories");
      setFC([]);
    }
  };

  useEffect(() => {
    fetchFC();
  }, []);

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view drinks categories" />
        </div>
      </div>
    );
  }

  const openCreate = () => {
    if (!canCreateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to create drinks category");
      return;
    }

    setEditingMenu(null);
    setDrawerOpen(true);
  };

  const openEdit = (menu) => {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update drinks category");
      return;
    }

    setEditingMenu(menu);
    setDrawerOpen(true);
  };

  const deleteMenu = async (id) => {
    if (!canDeleteModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to delete drinks category");
      return;
    }

    try {
      const data = await GeneralService.delete("drink_category", id);

      if (data?.status) {
        notify.success(data?.message || "Drinks category deleted successfully");
      } else {
        notify.error(data?.message || "Failed to delete drinks category");
      }

      fetchFC();
    } catch (error) {
      notify.error(error?.message || "Failed to delete drinks category");
      fetchFC();
    }
  };

  const handleSubmit = async (values) => {
    const req = {
      ...values,
      added_on: nowDate(),
      update_on: nowDate(),
    };

    const reqUpdate = {
      ...values,
      update_on: nowDate(),
    };

    if (editingMenu) {
      if (!canUpdateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to update drinks category");
        return;
      }

      await updateMenu("drink_category", editingMenu.id, reqUpdate);
      fetchFC();
    } else {
      if (!canCreateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to create drinks category");
        return;
      }

      await createMenu("drink_category", req);
      fetchFC();
    }

    setDrawerOpen(false);
  };

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <TagsOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Manage Drinks Category</h2>
            <p className="user-page-subtitle">
              Create, update, and manage drinks categories from one place.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            type="primary"
            className="primary-btn"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Create Drinks Category
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Drinks Category List</h3>
            <p>View and manage all available drinks categories.</p>
          </div>
        </div>

        <DCTable
          data={fC}
          loading={loading}
          onEdit={openEdit}
          onDelete={deleteMenu}
          canUpdate={canUpdateModule(MODULE_KEY)}
          canDelete={canDeleteModule(MODULE_KEY)}
        />
      </div>

      <CreateDrinkCat
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        initialValues={editingMenu}
      />
    </div>
  );
};

export default DrinkCat;